'use client';

import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import GlassButton from './GlassButton';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from './ThemeProvider';
import { useFinancialStore } from '../store/useFinancialStore';

export default function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { user, logout } = useFinancialStore();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const mainLinks = [
    { label: 'Terminal', path: '/dashboard' },
    { label: 'Ledger', path: '/portfolio' },
    { label: 'Market', path: '/market-watch' },
    { label: 'Signals', path: '/ai-signals' },
  ];

  const toolLinks = [
    { label: 'Neural Scan', path: '/image-analysis' },
    { label: 'Pattern Matrix', path: '/advanced-analysis' },
    { label: 'Quant Heuristics', path: '/advanced-indicators' },
  ];

  return (
    <nav className="sticky top-0 z-50 border-b border-white/5 bg-bg-primary/60 backdrop-blur-3xl">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Link to="/" className="text-2xl tracking-tighter font-black hover:text-brand-teal transition-all relative group flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-brand-teal flex items-center justify-center shadow-glow">
                  <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                </div>
                <span className="relative z-10 text-text-primary uppercase tracking-[0.2em]">RICH<span className="text-brand-teal-light font-light">GURU</span></span>
                <div className="absolute inset-0 bg-brand-teal/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </Link>
            </div>
          </div>
          
          {/* Desktop Links */}
          <div className="hidden lg:block ml-auto">
            <div className="flex items-center space-x-2">
              {mainLinks.map((link) => (
                <Link 
                  key={link.path}
                  to={link.path} 
                  className={`relative px-5 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all ${
                      isActive(link.path) 
                       ? 'text-brand-teal-light bg-brand-teal/10 shadow-glow-sm' 
                       : 'text-text-tertiary hover:text-text-primary hover:bg-white/5'
                  }`}
                >
                  {link.label}
                  {isActive(link.path) && (
                    <motion.div 
                      layoutId="nav-indicator"
                      className="absolute inset-x-4 -bottom-[1px] h-[2px] bg-brand-teal rounded-full shadow-glow"
                    />
                  )}
                </Link>
              ))}
              
              {/* Desktop Tools Dropdown */}
              <div className="relative group/dropdown px-2">
                <button className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest text-text-tertiary hover:text-text-primary hover:bg-white/5 transition-all">
                  Intelligence
                  <svg className="w-3.5 h-3.5 opacity-40 group-hover/dropdown:rotate-180 transition-transform duration-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div className="absolute right-0 top-full pt-4 opacity-0 invisible group-hover/dropdown:opacity-100 group-hover/dropdown:visible transition-all duration-500 translate-y-2 group-hover/dropdown:translate-y-0 z-50">
                  <div className="w-64 bg-bg-secondary/90 backdrop-blur-3xl rounded-3xl border border-white/5 p-3 shadow-2xl overflow-hidden relative">
                    <div className="absolute inset-0 bg-brand-teal/5 pointer-events-none" />
                    {toolLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        className="flex items-center gap-3 px-5 py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest text-text-tertiary hover:text-brand-teal-light hover:bg-brand-teal/10 transition-all group/item"
                      >
                         <div className="w-1.5 h-1.5 rounded-full bg-brand-teal/30 group-hover/item:bg-brand-teal transition-colors" />
                         {link.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="w-[1px] h-6 bg-white/5 mx-4" />
              
               <button
                 onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                 className="relative p-2.5 rounded-2xl border border-white/5 bg-white/[0.02] hover:bg-white/5 transition-all mr-4 overflow-hidden w-11 h-11 flex items-center justify-center text-text-primary shadow-glow-sm group"
               >
                 <AnimatePresence mode="wait" initial={false}>
                   {theme === 'dark' ? (
                     <motion.svg
                       key="moon"
                       initial={{ y: -20, opacity: 0, rotate: -90 }}
                       animate={{ y: 0, opacity: 1, rotate: 0 }}
                       exit={{ y: 20, opacity: 0, rotate: 90 }}
                       transition={{ duration: 0.3 }}
                       className="w-5 h-5 text-brand-teal-light group-hover:scale-110"
                       fill="none"
                       viewBox="0 0 24 24"
                       stroke="currentColor"
                     >
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                     </motion.svg>
                   ) : (
                     <motion.svg
                       key="sun"
                       initial={{ y: -20, opacity: 0, rotate: -90 }}
                       animate={{ y: 0, opacity: 1, rotate: 0 }}
                       exit={{ y: 20, opacity: 0, rotate: 90 }}
                       transition={{ duration: 0.3 }}
                       className="w-5 h-5 text-amber-400 group-hover:scale-110"
                       fill="none"
                       viewBox="0 0 24 24"
                       stroke="currentColor"
                     >
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                     </motion.svg>
                   )}
                 </AnimatePresence>
               </button>

                {user ? ( 
                  <div className="flex items-center gap-3">
                    <Link
                      to="/profile"
                      className="p-3 rounded-2xl text-text-tertiary hover:text-text-primary hover:bg-white/5 transition-all group border border-white/5"
                      title="User Protocol"
                    >
                      <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </Link>
                    <GlassButton
                      variant="ghost"
                      size="md"
                      onClick={() => {
                        logout().then(() => navigate('/auth/login'));
                      }}
                      className="text-[10px] font-black uppercase tracking-widest text-text-tertiary hover:text-brand-plum-light h-11 rounded-2xl px-6 border-white/5"
                    >
                      Logout
                    </GlassButton>
                  </div>
                ) : (
                 <GlassButton
                   asChild
                   variant="primary"
                   size="md"
                   glowEffect
                   className="h-12 rounded-2xl px-10 text-[10px] font-black uppercase tracking-[0.2em] shadow-glow"
                 >
                   <Link to="/auth/login">Access Network</Link>
                 </GlassButton>
               )}
             </div>
           </div>
           
           {/* Mobile menu controls */}
           <div className="lg:hidden flex items-center gap-4">
              <button
                 onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                 className="w-11 h-11 flex items-center justify-center rounded-2xl bg-white/5 border border-white/5 shadow-glow-sm"
               >
                  {theme === 'dark' ? (
                     <svg className="w-5 h-5 text-brand-teal-light" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                     </svg>
                   ) : (
                     <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                     </svg>
                   )}
               </button>
             <button 
               onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
               className="w-11 h-11 flex items-center justify-center rounded-2xl bg-white/5 border border-white/5 text-text-tertiary"
             >
               {isMobileMenuOpen ? (
                 <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                 </svg>
               ) : (
                 <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                 </svg>
               )}
             </button>
           </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="lg:hidden border-t border-white/5 bg-bg-secondary/95 backdrop-blur-3xl overflow-hidden"
          >
            <div className="px-6 py-10 space-y-4">
              {mainLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-6 py-5 rounded-3xl text-sm font-black uppercase tracking-[0.2em] transition-all ${
                    isActive(link.path)
                      ? 'bg-brand-teal/10 text-brand-teal-light shadow-glow-sm'
                      : 'text-text-tertiary hover:text-text-primary'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              
              <div className="pt-6 pb-2 text-[10px] font-black uppercase tracking-[0.3em] text-text-tertiary px-6 opacity-40">
                Advanced Intelligence
              </div>
              
              {toolLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-6 py-5 rounded-3xl text-sm font-black uppercase tracking-[0.2em] transition-all ${
                    isActive(link.path)
                      ? 'bg-brand-teal/10 text-brand-teal-light shadow-glow-sm'
                      : 'text-text-tertiary hover:text-text-primary'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-8 border-t border-white/5 mt-4">
                {user ? (
                  <button
                    onClick={() => {
                      logout().then(() => {
                        setIsMobileMenuOpen(false);
                        navigate('/auth/login');
                      });
                    }}
                    className="w-full text-center p-6 rounded-3xl bg-brand-plum/10 text-brand-plum-light font-black uppercase tracking-[0.2em] text-xs"
                  >
                    Logout Protocol
                  </button>
                ) : (
                  <Link
                    to="/auth/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block p-6 bg-brand-teal text-white rounded-3xl text-center font-black uppercase tracking-[0.2em] text-xs shadow-glow"
                  >
                    Gain Access
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}